import React from 'react';

function AboutPage({ darkMode }) {
  const cardClass = darkMode 
    ? 'bg-gray-800 text-white' 
    : 'bg-white text-gray-800';

  return (
    <div className="min-h-screen py-12 px-6">
      <div className="max-w-4xl mx-auto">
        {/* 헤더 섹션 */}
        <div className="text-center mb-12">
          <div className="text-6xl mb-4 animate-bounce">🎯</div>
          <h1 className="text-4xl md:text-5xl font-black mb-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            서비스 소개
          </h1>
          <p className={`text-xl ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            취업·입시 면접 완전 정복 시스템
          </p>
        </div>

        {/* 메인 소개 카드 */}
        <div className={`${cardClass} rounded-2xl shadow-2xl p-8 md:p-12 mb-8`}>
          <h2 className="text-2xl md:text-3xl font-bold mb-6 text-center">
            🎯 서비스 소개
          </h2>
          <p className="text-lg leading-relaxed mb-6">
            <strong>면접톡</strong>은 취업 준비생 및 입시 준비생들이 언제 어디서나 효과적으로 면접을 연습할 수 있도록 도와주는 무료 웹 서비스입니다.
          </p>
          
          <div className={`p-6 rounded-xl ${darkMode ? 'bg-blue-900 bg-opacity-30' : 'bg-blue-50'} mb-6`}>
            <h3 className="text-xl font-bold mb-4 flex items-center">
              <span className="text-2xl mr-2">💡</span>
              개발 배경
            </h3>
            <p className="leading-relaxed mb-4">
              면접은 충분한 연습이 필요하지만, 혼자서 체계적으로 연습하기는 쉽지 않습니다.
            </p>
            <ul className="space-y-2 text-sm">
              <li>❌ 다양한 유형의 질문을 누가 해줄 사람이 없어요</li>
              <li>❌ 한정된 시간 안에서 답변하는 연습이 어려워요</li>
              <li>❌ 기본부터 심화까지 단계별 연습이 필요해요</li>
              <li>❌ 직무별 전문 질문과 상황 대응 연습이 부족해요</li>
              <li>❌ 입시면접 누가 도와줄수 없을까요?</li>
            </ul>
          </div>
          
          <div className="text-center">
            <p className="text-lg font-semibold text-blue-600">
              이런 문제들을 해결하기 위해 탄생한 것이 바로<br/>
              <span className="text-2xl font-black">취업·입시 체계적 면접 연습 서비스</span>입니다! 🚀
            </p>
          </div>
        </div>

        {/* 주요 특징 섹션 */}
        <div className={`${cardClass} rounded-2xl shadow-2xl p-8 md:p-12 mb-8`}>
          <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center">
            ⭐ 주요 특징
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* 음성 기반 실전 연습 */}
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-purple-900 bg-opacity-30' : 'bg-purple-50'}`}>
              <h3 className="text-xl font-bold mb-4 flex items-center">
                <span className="text-2xl mr-2">🎤</span>
                음성 기반 실전 연습
              </h3>
              <ul className="space-y-2 text-sm leading-relaxed">
                <li>• 실제 면접관이 질문하는 것처럼 음성으로 질문 제공</li>
                <li>• 음성 인식으로 답변을 인식하여 실전과 동일한 환경</li>
                <li>• 본인 휴대폰 녹음 기능으로 답변 내용 복습 가능</li>
                <li>• 음성 데이터는 저장되지 않아 안전하게 연습 가능</li>
              </ul>
            </div>

            {/* 한정된 시간 실전 훈련 */}
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-orange-900 bg-opacity-30' : 'bg-orange-50'}`}>
              <h3 className="text-xl font-bold mb-4 flex items-center">
                <span className="text-2xl mr-2">⏰</span>
                한정된 시간 실전 훈련
              </h3>
              <ul className="space-y-2 text-sm leading-relaxed">
                <li>• 질문당 45초 제한으로 간결하고 명확한 답변 연습</li>
                <li>• 실제 면접의 시간 압박감 체험 및 적응</li>
                <li>• 시간 연장 기능으로 단계별 연습 가능</li>
                <li>• 반복 연습으로 시간 감각 완벽 습득</li>
              </ul>
            </div>
          </div>
        </div>

        {/* 4단계 시스템 섹션 */}
        <div className={`${cardClass} rounded-2xl shadow-2xl p-8 md:p-12 mb-8`}>
          <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center">
            📚 취업면접 4단계 체계적 완전 정복
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* 기본질문 */}
            <div className={`p-6 rounded-xl border-l-4 border-blue-500 ${darkMode ? 'bg-blue-900 bg-opacity-20' : 'bg-blue-50'}`}>
              <h3 className="text-xl font-bold mb-3 flex items-center">
                <span className="text-2xl mr-2">🔰</span>
                기본질문 (10문항)
              </h3>
              <ul className="space-y-1 text-sm">
                <li>• 자기소개, 지원동기, 입사 후 포부 등</li>
                <li>• 모든 면접의 기본이 되는 필수 질문들</li>
                <li>• 면접 초보자도 안심하고 시작할 수 있는 기본기 완성</li>
              </ul>
            </div>

            {/* 심화질문 */}
            <div className={`p-6 rounded-xl border-l-4 border-purple-500 ${darkMode ? 'bg-purple-900 bg-opacity-20' : 'bg-purple-50'}`}>
              <h3 className="text-xl font-bold mb-3 flex items-center">
                <span className="text-2xl mr-2">🎯</span>
                심화질문 (10문항)
              </h3>
              <ul className="space-y-1 text-sm">
                <li>• 가치관, 성격, 경험 등 깊이 있는 질문</li>
                <li>• 단순 암기가 아닌 진정성 있는 답변 연습</li>
                <li>• 면접관이 지원자를 제대로 파악하려는 심층 질문들</li>
              </ul>
            </div>

            {/* 직무질문 */}
            <div className={`p-6 rounded-xl border-l-4 border-green-500 ${darkMode ? 'bg-green-900 bg-opacity-20' : 'bg-green-50'}`}>
              <h3 className="text-xl font-bold mb-3 flex items-center">
                <span className="text-2xl mr-2">💼</span>
                직무질문 (38개 직무별 각 10문항)
              </h3>
              <ul className="space-y-1 text-sm">
                <li>• 개발자, 마케팅, 인사, 영업, 디자인 등 전문 직무별 맞춤 질문</li>
                <li>• 해당 분야 실무진이 실제로 묻는 전문적인 질문들</li>
                <li>• 직무 역량과 전문성을 제대로 어필할 수 있는 질문 구성</li>
              </ul>
            </div>

            {/* 상황질문 */}
            <div className={`p-6 rounded-xl border-l-4 border-orange-500 ${darkMode ? 'bg-orange-900 bg-opacity-20' : 'bg-orange-50'}`}>
              <h3 className="text-xl font-bold mb-3 flex items-center">
                <span className="text-2xl mr-2">🧩</span>
                상황질문 (10문항)
              </h3>
              <ul className="space-y-1 text-sm">
                <li>• "만약 ~라면 어떻게 하시겠습니까?" 형태의 상황 대응 질문</li>
                <li>• 문제해결능력, 판단력, 리더십 등을 평가하는 실무형 질문</li>
                <li>• 실제 업무에서 마주할 수 있는 다양한 상황별 대응력 훈련</li>
              </ul>
            </div>
          </div>
        </div>

        {/* 🆕 입시면접 섹션 (별도) */}
        <div className={`${cardClass} rounded-2xl shadow-2xl p-8 md:p-12 mb-8`}>
          <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center">
            🎓 입시면접 준비 🆕
          </h2>
          
          <div className={`p-6 rounded-xl border-l-4 border-pink-500 ${darkMode ? 'bg-pink-900 bg-opacity-20' : 'bg-pink-50'}`}>
            <h3 className="text-xl font-bold mb-3 flex items-center">
              <span className="text-2xl mr-2">🎓</span>
              입시면접 (15문항)
            </h3>
            <ul className="space-y-2 text-sm">
              <li>• 대학 입시면접 전문 질문 (수시·정시 완벽 대비)</li>
              <li>• 자기소개, 지원동기, 학업계획, 진로계획, 독서 경험 등</li>
              <li>• 고등학생 눈높이에 맞춘 질문 구성</li>
              <li>• 실전과 동일한 45초 답변 시간 연습</li>
            </ul>
          </div>
          
          <div className={`mt-6 p-4 rounded-lg text-center ${darkMode ? 'bg-pink-900 bg-opacity-30' : 'bg-pink-50'}`}>
            <p className="text-sm font-semibold text-pink-600">
              💡 취업면접과 입시면접, 목적에 맞게 선택해서 연습하세요!
            </p>
          </div>
        </div>

        {/* 차별화 포인트 섹션 */}
        <div className={`${cardClass} rounded-2xl shadow-2xl p-8 md:p-12 mb-8`}>
          <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center">
            🏆 차별화 포인트
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-yellow-900 bg-opacity-30' : 'bg-yellow-50'}`}>
              <h3 className="text-lg font-bold mb-3">
                1️⃣ 체계적 4단계 면접 완전 정복
              </h3>
              <p className="text-sm">다른 서비스와 달리 기본→심화→직무→상황 순서로 단계별 완벽 준비 가능</p>
            </div>

            <div className={`p-6 rounded-xl ${darkMode ? 'bg-red-900 bg-opacity-30' : 'bg-red-50'}`}>
              <h3 className="text-lg font-bold mb-3">
                2️⃣ 한정된 시간 실전 훈련의 핵심
              </h3>
              <p className="text-sm">실제 면접의 시간 압박감을 그대로 재현하여 진짜 실력 향상</p>
            </div>

            <div className={`p-6 rounded-xl ${darkMode ? 'bg-indigo-900 bg-opacity-30' : 'bg-indigo-50'}`}>
              <h3 className="text-lg font-bold mb-3">
                3️⃣ 전문 직업상담사의 실무 노하우
              </h3>
              <p className="text-sm">이론이 아닌 실제 면접 현장에서 자주 나오는 질문들로만 구성</p>
            </div>

            <div className={`p-6 rounded-xl ${darkMode ? 'bg-pink-900 bg-opacity-30' : 'bg-pink-50'}`}>
              <h3 className="text-lg font-bold mb-3">
                4️⃣ 취업·입시 모두 대비 가능 🆕
              </h3>
              <p className="text-sm">취업면접 4단계부터 대학입시면접까지, 어떤 면접도 완벽 준비</p>
            </div>
          </div>
        </div>

        {/* 추천 대상 섹션 */}
        <div className={`${cardClass} rounded-2xl shadow-2xl p-8 md:p-12 mb-8`}>
          <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center">
            🎓 이런 분들께 추천
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-4xl mb-4">👨‍🎓</div>
              <h3 className="text-lg font-bold mb-3">취업·입시 준비생</h3>
              <ul className="text-sm space-y-1">
                <li>• 첫 면접을 앞둔 신입 구직자</li>
                <li>• 이직을 준비하는 경력직</li>
                <li>• 대학 입시면접 준비 고3 수험생 🆕</li>
                <li>• 수시·정시 면접 대비 학생 🆕</li>
              </ul>
            </div>

            <div className="text-center">
              <div className="text-4xl mb-4">💼</div>
              <h3 className="text-lg font-bold mb-3">직무별 준비생</h3>
              <ul className="text-sm space-y-1">
                <li>• IT/개발직 지원자</li>
                <li>• 공공기관 지원자</li>
                <li>• 대기업/중소기업 지원자</li>
                <li>• 서비스업 지원자</li>
              </ul>
            </div>

            <div className="text-center">
              <div className="text-4xl mb-4">⏰</div>
              <h3 className="text-lg font-bold mb-3">효율적 연습을 원하는 분</h3>
              <ul className="text-sm space-y-1">
                <li>• 짧은 시간에 집중적으로 연습</li>
                <li>• 혼자서도 체계적으로 준비</li>
                <li>• 반복 연습으로 자신감 향상</li>
              </ul>
            </div>
          </div>
        </div>

        {/* 사용법 섹션 */}
        <div className={`${cardClass} rounded-2xl shadow-2xl p-8 md:p-12 mb-8`}>
          <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center">
            🚀 사용법
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-blue-500 text-white rounded-full flex items-center justify-center text-2xl font-bold">1</div>
              <h3 className="font-bold mb-2">질문 유형 선택</h3>
              <p className="text-sm">취업면접 or 입시면접 중 원하는 유형 선택</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-purple-500 text-white rounded-full flex items-center justify-center text-2xl font-bold">2</div>
              <h3 className="font-bold mb-2">면접 시작</h3>
              <p className="text-sm">질문이 음성으로 나오면 마이크에 대고 답변</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-orange-500 text-white rounded-full flex items-center justify-center text-2xl font-bold">3</div>
              <h3 className="font-bold mb-2">시간 관리</h3>
              <p className="text-sm">45초 내에 간결하고 명확한 답변 완료</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-green-500 text-white rounded-full flex items-center justify-center text-2xl font-bold">4</div>
              <h3 className="font-bold mb-2">체계적 반복 연습</h3>
              <p className="text-sm">모든 유형을 마스터할 때까지 반복</p>
            </div>
          </div>
        </div>

        {/* 운영 철학 섹션 */}
        <div className={`${cardClass} rounded-2xl shadow-2xl p-8 md:p-12 mb-8`}>
          <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center">
            🌟 운영 철학
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-teal-900 bg-opacity-30' : 'bg-teal-50'}`}>
              <h3 className="text-lg font-bold mb-3 flex items-center">
                <span className="text-2xl mr-2">🎯</span>
                사용자 중심
              </h3>
              <p className="text-sm">이용자의 편의와 효과적인 학습을 최우선으로 고려</p>
            </div>

            <div className={`p-6 rounded-xl ${darkMode ? 'bg-red-900 bg-opacity-30' : 'bg-red-50'}`}>
              <h3 className="text-lg font-bold mb-3 flex items-center">
                <span className="text-2xl mr-2">🔒</span>
                프라이버시 보호
              </h3>
              <p className="text-sm">개인정보 수집 최소화 및 안전한 서비스 제공</p>
            </div>

            <div className={`p-6 rounded-xl ${darkMode ? 'bg-blue-900 bg-opacity-30' : 'bg-blue-50'}`}>
              <h3 className="text-lg font-bold mb-3 flex items-center">
                <span className="text-2xl mr-2">📈</span>
                지속적 개선
              </h3>
              <p className="text-sm">사용자 피드백을 바탕으로 한 서비스 품질 향상</p>
            </div>

            <div className={`p-6 rounded-xl ${darkMode ? 'bg-green-900 bg-opacity-30' : 'bg-green-50'}`}>
              <h3 className="text-lg font-bold mb-3 flex items-center">
                <span className="text-2xl mr-2">🤝</span>
                무료 서비스
              </h3>
              <p className="text-sm">누구나 부담 없이 이용할 수 있는 공익적 서비스 지향</p>
            </div>
          </div>
        </div>

        {/* 마무리 섹션 */}
        <div className="text-center">
          <div className={`${cardClass} rounded-2xl shadow-2xl p-8 md:p-12`}>
            <div className="text-6xl mb-6">🎉</div>
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              여러분의 면접 완전 정복을 진심으로 응원합니다!
            </h2>
            <p className="text-lg font-semibold text-blue-600 mb-6">
              "취업면접부터 입시면접까지, 완벽 준비로 면접 마스터가 되세요!"
            </p>
            <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              체계적 연습으로 면접 성공의 그날까지! ✨
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AboutPage;